import './i18n'

import React from 'react'
import { createRoot } from 'react-dom/client'

import App from './App'
import { router } from './router'
import { setupVersionCheck } from './lib/versionCheck'
import { installEagerGlobalHandlers } from './sentry/globalHandlers'

// Capture async errors that fire before the deferred Sentry SDK loads
// (see ./sentry/init). Removed again once the real SDK binds its own handlers.
if (import.meta.env.PROD) {
  installEagerGlobalHandlers()
}

const container = document.getElementById('root')
if (!container) {
  throw new Error('Root container #root not found in index.html')
}

createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)

// Reload immediately if a route's chunk can't be loaded — typically the
// running tab is on an old build whose chunks have been purged after a deploy.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  window.location.reload()
})

// Poll the backend's /api/version against the SHA baked into this bundle.
// On mismatch the next React Router navigation triggers a hard reload, so
// the user picks up the new bundle without losing their current view.
setupVersionCheck(router, __BUILD_SHA__)

// Defer the @sentry/react SDK (~75 KB gz) out of the first-load chunk.
// The eager `src/sentry/buffer.ts` + `<ErrorBoundary>` capture errors into
// a queue while we wait, then `src/sentry/init.ts` calls Sentry.init and
// drains the queue once the browser is idle.
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  const idle =
    typeof window !== 'undefined' && 'requestIdleCallback' in window
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).requestIdleCallback
      : (cb: () => void) => setTimeout(cb, 1)
  idle(() => {
    void import('./sentry/init')
  })
}
